/**
 * ==========================================================================
 * LF · Selector de mercado — assets/lf-geo-popup.js
 * --------------------------------------------------------------------------
 * Lógica del popup de geolocalización. Se carga con "defer" desde
 * sections/lf-geo-popup.liquid. Sin dependencias, unos 4 KB minificado.
 *
 * Flujo:
 *  0) Modo "una vez al día": en la primera página de cada día se muestra
 *     el popup a TODOS (con su última elección preseleccionada si la hay).
 *  A) El visitante YA eligió antes (localStorage):
 *     - Si entra desde fuera (Google, anuncio, email) y está en otro
 *       mercado → redirección automática a su elección (opcional).
 *     - Si navega dentro de la tienda y ha cambiado de mercado con otro
 *       selector (p. ej. el del footer) → actualizamos su elección.
 *  B) Primera visita:
 *     - Detectamos país por IP (endpoint nativo de Shopify) e idioma del
 *       navegador → preseleccionamos y abrimos el popup.
 *
 * Eventos que emite (para GA4/dataLayer si se quiere medir):
 *  - 'lf-geo:open'     → se mostró el popup
 *  - 'lf-geo:choice'   → el usuario eligió { country, language }
 *  - 'lf-geo:redirect' → redirección automática { country, language }
 * ==========================================================================
 */
(() => {
  'use strict';

  // Evita definir el elemento dos veces (p. ej. al recargar la sección en el editor)
  if (customElements.get('lf-geo-popup')) return;

  /* ------------------------------------------------------------------------
   * Constantes y utilidades
   * ---------------------------------------------------------------------- */
  const STORAGE_KEY = 'lf_geo_choice';      // elección guardada (localStorage)
  const REDIRECT_FLAG = 'lf_geo_redirected'; // evita bucles (sessionStorage)
  const DETECT_TIMEOUT = 2500;               // ms máx. esperando a la detección por IP
  const MARK_PARAM = 'lf_geo';               // parámetro temporal tras redirigir (se borra al llegar)
  const SHOWN_KEY = 'lf_geo_last_shown';     // último día en que se mostró el popup (modo diario)

  // Bots y herramientas de test: NUNCA mostramos popup ni redirigimos (SEO + Lighthouse)
  const BOT_RE = /bot|crawl|spider|slurp|lighthouse|pagespeed|headless|preview|facebookexternalhit|embedly|whatsapp/i;

  // localStorage / sessionStorage con try/catch (Safari privado, cookies bloqueadas…)
  const store = {
    get(key, session = false) {
      try {
        const raw = (session ? sessionStorage : localStorage).getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    set(key, value, session = false) {
      try {
        (session ? sessionStorage : localStorage).setItem(key, JSON.stringify(value));
      } catch (e) {
        /* sin almacenamiento: el popup simplemente volverá a salir */
      }
    },
  };

  // Normaliza códigos de idioma para compararlos: "pt-BR" → "pt-br"
  const norm = (code) => String(code || '').toLowerCase();

  // "es-ES" → "es"
  const base = (code) => norm(code).split('-')[0];

  // Fecha local de hoy en formato AAAA-MM-DD (cambia a medianoche del visitante)
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  /* ------------------------------------------------------------------------
   * Custom Element <lf-geo-popup>
   * ---------------------------------------------------------------------- */
  class LfGeoPopup extends HTMLElement {
    connectedCallback() {
      // 1. Referencias al DOM
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.countryInput = this.querySelector('[data-lf-geo-country]');
      this.languageSelect = this.querySelector('[data-lf-geo-language]');
      this.languageField = this.querySelector('[data-lf-geo-language-field]');
      this.flagImg = this.querySelector('[data-lf-geo-flag]');

      // 2. Datos generados en Liquid (países, idiomas, textos)
      try {
        this.data = JSON.parse(this.querySelector('[data-lf-geo-data]').textContent);
      } catch (e) {
        console.warn('[LF Geo] No se pudieron leer los datos del popup', e);
        return;
      }

      // 3. Configuración desde los data-* de la sección
      const ds = this.dataset;
      this.settings = {
        mode: ds.mode,                                  // 'daily' | 'always' | 'mismatch'
        autoRedirect: ds.autoRedirect === 'true',
        delay: parseInt(ds.delay, 10) || 0,
        showLanguage: ds.showLanguage === 'true',
        showCurrency: ds.showCurrency === 'true',
        designMode: ds.designMode === 'true',
      };
      this.current = { country: ds.currentCountry, language: ds.currentLanguage };
      this.root = ds.root || '/';
      this.texts = this.data.texts && this.data.texts.length ? this.data.texts : null;

      this.bindEvents();

      // En el editor de temas no abrimos solo: se abre al seleccionar la sección
      if (this.settings.designMode) {
        this.bindEditorEvents();
        return;
      }

      this.init();
    }

    /* ----------------------------------------------------------------------
     * Arranque
     * -------------------------------------------------------------------- */
    async init() {
      if (this.isBot()) return;

      // ---------- 0) Venimos de una redirección hecha por este popup ----------
      // Marcamos la URL con ?lf_geo=1 al redirigir. Así funciona también con
      // mercados en dominios distintos (.es → .fr), donde localStorage no se comparte.
      const url = new URL(window.location.href);
      if (url.searchParams.has(MARK_PARAM)) {
        this.saveChoice(this.current);            // ya estamos en el mercado elegido
        this.markShownToday();                    // ya eligió hoy: no volver a preguntar hoy
        url.searchParams.delete(MARK_PARAM);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        return;
      }

      const stored = store.get(STORAGE_KEY);

      // ---------- Modo "una vez al día": primera página del día → popup a todos ----------
      if (this.settings.mode === 'daily' && store.get(SHOWN_KEY) !== today()) {
        let country;
        let language;
        if (stored && stored.country && !stored.dismissed && this.data.countries[stored.country]) {
          // Ya eligió otro día: preseleccionamos su elección
          country = stored.country;
          language = this.pickLanguage(country, stored.language);
        } else {
          // Nunca eligió: preseleccionamos lo detectado (IP + idioma del navegador)
          const detected = await this.detectCountry();
          country = detected && this.data.countries[detected] ? detected : this.current.country;
          language = this.pickLanguage(country);
        }
        this.prefill(country, language);
        window.setTimeout(() => this.open(), this.settings.delay);
        return;
      }

      // ---------- A) Visitante que ya eligió ----------
      if (stored && stored.country) {
        const differs = !this.sameAsCurrent(stored);

        if (differs && !this.isEntryPage()) {
          // Ha cambiado de mercado navegando (otro selector): respetamos su cambio
          this.saveChoice(this.current);
          return;
        }

        if (
          differs &&
          this.settings.autoRedirect &&
          !stored.dismissed &&                 // si cerró el popup sin elegir, no forzamos
          this.data.countries[stored.country] &&
          !store.get(REDIRECT_FLAG, true)      // máx. 1 redirección por sesión (anti-bucle)
        ) {
          store.set(REDIRECT_FLAG, true, true);
          this.emit('lf-geo:redirect', stored);
          this.submitChoice(stored);
        }
        return;
      }

      // ---------- B) Primera visita ----------
      const detected = await this.detectCountry();

      // Si no vendemos en el país detectado (o no se detecta), proponemos el actual
      const country = detected && this.data.countries[detected] ? detected : this.current.country;
      const language = this.pickLanguage(country);

      // Modo "solo si no coincide": si ya está bien localizado, no molestamos
      if (
        this.settings.mode === 'mismatch' &&
        country === this.current.country &&
        norm(language) === norm(this.current.language)
      ) {
        this.saveChoice(this.current);
        return;
      }

      this.prefill(country, language);
      window.setTimeout(() => this.open(), this.settings.delay);
    }

    /* ----------------------------------------------------------------------
     * Eventos de la interfaz
     * -------------------------------------------------------------------- */
    bindEvents() {
      // Botón X
      this.querySelectorAll('[data-lf-geo-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.dismiss())
      );

      // Tecla Esc (el <dialog> lanza 'cancel')
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.dismiss();
      });

      // Clic fuera del panel (sobre el fondo oscuro)
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dismiss();
      });

      // Cambio de país → repintar idiomas y bandera
      if (this.countryInput.tagName === 'SELECT') {
        this.countryInput.addEventListener('change', () => {
          this.renderLanguages(this.countryInput.value, this.languageSelect.value);
          this.updateFlag(this.countryInput.value);
        });
      }

      // Envío del formulario
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));

      // Abrir manualmente desde cualquier enlace/botón del tema:
      //   <button data-lf-geo-open>Cambiar país</button>  o  <a href="#lf-geo">…</a>
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-lf-geo-open], a[href="#lf-geo"]');
        if (!trigger) return;
        e.preventDefault();
        this.prefill(this.current.country, this.current.language);
        this.open();
      });

      // API global por si se quiere abrir desde otro script: window.LFGeo.open()
      window.LFGeo = { open: () => { this.prefill(this.current.country, this.current.language); this.open(); } };
    }

    // Editor de temas: abrir/cerrar al seleccionar la sección o un bloque de textos
    bindEditorEvents() {
      const isMine = (e) => e.detail && e.detail.sectionId === this.dataset.sectionId;

      document.addEventListener('shopify:section:select', (e) => {
        if (!isMine(e)) return;
        this.prefill(this.current.country, this.current.language);
        this.open();
      });
      document.addEventListener('shopify:section:load', (e) => {
        if (!isMine(e)) return;
        this.prefill(this.current.country, this.current.language);
        this.open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (isMine(e)) this.close();
      });
      document.addEventListener('shopify:block:select', (e) => {
        if (!isMine(e) || !this.texts) return;
        const block = this.texts.find((t) => t.id === e.detail.blockId);
        if (!block) return;
        this.prefill(this.current.country, this.current.language);
        this.applyTexts(block, this.current.country); // previsualiza ese idioma
        this.open();
      });
    }

    handleSubmit(e) {
      e.preventDefault();
      const choice = { country: this.countryInput.value, language: this.languageSelect.value };

      this.saveChoice(choice);
      this.emit('lf-geo:choice', choice);

      // Ya está en ese mercado/idioma → solo cerramos
      if (this.sameAsCurrent(choice)) {
        this.close();
        return;
      }
      this.submitChoice(choice);
    }

    /* ----------------------------------------------------------------------
     * Detección
     * -------------------------------------------------------------------- */

    // País por IP con el endpoint oficial de Shopify Markets.
    // Devuelve "ES", "FR"… o null si falla o tarda demasiado.
    async detectCountry() {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), DETECT_TIMEOUT);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.root;

      try {
        const res = await fetch(
          `${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`,
          { signal: controller.signal, headers: { Accept: 'application/json' } }
        );
        if (!res.ok) return null;
        const json = await res.json();
        const code = json && json.detected_values && json.detected_values.country && json.detected_values.country.handle;
        return code ? String(code).toUpperCase() : null;
      } catch (e) {
        return null; // sin red, bloqueado o timeout → seguimos sin detección
      } finally {
        window.clearTimeout(timer);
      }
    }

    // Elige el mejor idioma para un país según el navegador del visitante
    pickLanguage(countryCode, preferred) {
      const langs = this.getLanguages(countryCode).map((l) => l.code);
      if (!langs.length) return this.current.language;

      const wanted = [preferred, ...(navigator.languages || [navigator.language])].filter(Boolean);

      // 1º coincidencia exacta (pt-BR = pt-BR), 2º por idioma base (es-ES → es)
      for (const w of wanted) {
        const exact = langs.find((l) => norm(l) === norm(w));
        if (exact) return exact;
        const partial = langs.find((l) => base(l) === base(w));
        if (partial) return partial;
      }
      // Si no hay coincidencia: el idioma actual si existe en ese país, si no el primero
      return langs.find((l) => norm(l) === norm(this.current.language)) || langs[0];
    }

    // Bloque de textos para el popup: idioma del navegador → idioma actual → primero
    pickTexts() {
      if (!this.texts) return null;
      const wanted = [...(navigator.languages || [navigator.language]), this.current.language];
      for (const w of wanted) {
        const exact = this.texts.find((t) => t.lang === norm(w));
        if (exact) return exact;
        const partial = this.texts.find((t) => base(t.lang) === base(w));
        if (partial) return partial;
      }
      return this.texts[0];
    }

    /* ----------------------------------------------------------------------
     * Pintado del popup
     * -------------------------------------------------------------------- */
    prefill(country, language) {
      if (this.countryInput.tagName === 'SELECT') this.countryInput.value = country;
      this.renderLanguages(country, language);
      this.updateFlag(country);

      const texts = this.pickTexts();
      if (texts) this.applyTexts(texts, country);
    }

    // Idiomas del mercado al que pertenece el país elegido
    getLanguages(countryCode) {
      const c = this.data.countries[countryCode];
      return c && c.languages ? c.languages : [];
    }

    renderLanguages(countryCode, preferred) {
      const langs = this.getLanguages(countryCode);
      if (!langs.length) return; // dejamos las opciones del servidor

      const selected = this.pickLanguage(countryCode, preferred);
      this.languageSelect.innerHTML = '';
      langs.forEach((l) => {
        const opt = document.createElement('option');
        opt.value = l.code;
        opt.lang = l.code;
        opt.textContent = l.name; // nombre en su propio idioma: "Español", "English"
        if (l.code === selected) opt.selected = true;
        this.languageSelect.appendChild(opt);
      });

      // Si solo hay un idioma (o está desactivado) ocultamos el campo.
      // OJO: oculto ≠ disabled. Oculto sigue enviándose en el formulario.
      this.languageField.hidden = !this.settings.showLanguage || langs.length < 2;
    }

    updateFlag(countryCode) {
      const c = this.data.countries[countryCode];
      if (this.flagImg && c && c.flag) this.flagImg.src = c.flag;
    }

    // Pone los textos en el idioma del visitante y traduce los nombres de país
    applyTexts(texts, countryCode) {
      this.dialog.setAttribute('lang', texts.lang || '');
      const countryName = this.countryName(countryCode, texts.lang);

      this.querySelectorAll('[data-lf-geo-text]').forEach((el) => {
        const key = el.getAttribute('data-lf-geo-text');
        let value = texts[key];
        if (!value) return;
        if (key === 'message') value = value.replace(/\{country\}/g, countryName);
        el.textContent = value; // textContent: nunca innerHTML con textos editables
      });

      const closeBtn = this.querySelector('[data-lf-geo-text-attr="close_label"]');
      if (closeBtn && texts.close_label) closeBtn.setAttribute('aria-label', texts.close_label);

      this.relabelCountries(texts.lang);
    }

    // Nombre del país en el idioma del popup (API nativa Intl, sin peticiones)
    countryName(code, lang) {
      try {
        const name = new Intl.DisplayNames([lang], { type: 'region' }).of(code);
        if (name && name !== code) return name;
      } catch (e) { /* navegador antiguo o código raro */ }
      const c = this.data.countries[code];
      return c ? c.name : code;
    }

    // Traduce y reordena alfabéticamente las opciones de país al idioma del popup
    relabelCountries(lang) {
      if (this.countryInput.tagName !== 'SELECT') return;
      const options = Array.from(this.countryInput.options);
      const selected = this.countryInput.value;

      options.forEach((opt) => {
        const c = this.data.countries[opt.value];
        const name = this.countryName(opt.value, lang);
        opt.textContent = this.settings.showCurrency && c ? `${name} (${c.currency} ${c.symbol})` : name;
      });

      options
        .sort((a, b) => a.textContent.localeCompare(b.textContent, lang))
        .forEach((opt) => this.countryInput.appendChild(opt)); // appendChild mueve, no duplica

      this.countryInput.value = selected;
    }

    /* ----------------------------------------------------------------------
     * Abrir / cerrar / redirigir
     * -------------------------------------------------------------------- */
    open() {
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('lf-geo-lock'); // bloquea el scroll del fondo
      if (!this.settings.designMode) this.markShownToday();
      this.emit('lf-geo:open', { country: this.countryInput.value, language: this.languageSelect.value });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('lf-geo-lock');
    }

    // Cierre sin elegir: si nunca había elegido, se queda en el mercado actual
    // y no se le redirige automáticamente. Si ya tenía una elección, se respeta.
    dismiss() {
      if (!this.settings.designMode && !store.get(STORAGE_KEY)) {
        this.saveChoice({ ...this.current, dismissed: true });
      }
      this.close();
    }

    // Envía el formulario nativo → Shopify redirige al mercado correcto
    submitChoice(choice) {
      if (this.countryInput.tagName === 'SELECT') this.countryInput.value = choice.country;
      this.renderLanguages(choice.country, choice.language);

      // return_to: misma página + parámetros (UTM, gclid…), sin el prefijo de idioma actual
      const returnTo = this.form.querySelector('input[name="return_to"]');
      if (returnTo) returnTo.value = this.returnPath();

      // form.submit() no dispara el evento 'submit' → no hay bucle con handleSubmit
      this.form.submit();
    }

    // "/en/products/camiseta?utm_source=ig" → "/products/camiseta?utm_source=ig&lf_geo=1"
    returnPath() {
      const root = this.root.endsWith('/') ? this.root : `${this.root}/`;
      let path = window.location.pathname;
      if (root !== '/' && `${path}/`.startsWith(root)) {
        path = `/${path.slice(root.length)}`;
      }
      const params = new URLSearchParams(window.location.search); // conserva UTM, gclid, fbclid…
      params.set(MARK_PARAM, '1');
      return `${path}?${params.toString()}`;
    }

    /* ----------------------------------------------------------------------
     * Helpers
     * -------------------------------------------------------------------- */
    markShownToday() {
      store.set(SHOWN_KEY, today());
    }

    saveChoice(choice) {
      store.set(STORAGE_KEY, {
        country: choice.country,
        language: choice.language,
        dismissed: Boolean(choice.dismissed),
        ts: Date.now(),
      });
    }

    sameAsCurrent(choice) {
      return choice.country === this.current.country && norm(choice.language) === norm(this.current.language);
    }

    // ¿Entra a la tienda desde fuera? (sin referrer o referrer de otro dominio)
    isEntryPage() {
      if (!document.referrer) return true;
      try {
        return new URL(document.referrer).hostname !== window.location.hostname;
      } catch (e) {
        return true;
      }
    }

    isBot() {
      return Boolean(navigator.webdriver) || BOT_RE.test(navigator.userAgent || '');
    }

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }
  }

  customElements.define('lf-geo-popup', LfGeoPopup);
})();